/**
 * ListingPage Component
 *
 * Main listing detail page combining all sections.
 */

import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts';
import { PublicLayout, Container } from '../../components/layout';
import {
  PhotoGallery,
  PropertyInfo,
  Description,
  Amenities,
  HostCard,
  HouseRules,
  Reviews,
  BookingWidget,
  MobileBookingBar,
  LocationMap,
} from './components';
import { useListing } from './hooks';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { SEO_ORIGIN } from '@/components/common/Seo';

export const ListingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { listing, isLoading, error } = useListing(id || '');
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);

  // SEO: unique title / description / canonical per listing.
  const seoCity = listing?.location?.city || listing?.location?.area || 'Lebanon';
  useDocumentMeta({
    title: listing
      ? `${listing.title} - ${seoCity}, Lebanon | DARAK`
      : 'Vacation rental | DARAK',
    description: listing
      ? (listing.description ||
          `Book ${listing.title} in ${seoCity}, Lebanon on DARAK.`)
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 155)
      : undefined,
    canonical: `${SEO_ORIGIN}/listing/${id}`,
    ogImage: listing?.photos?.[0]?.url,
  });

  const handleContactHost = async () => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }

    if (!listing) return;
    const hostId = listing.host.id;

    // Don't message yourself
    if (user.uid === hostId) return;

    try {
      // Check for existing thread between this user and host for this listing
      const threadsRef = collection(db, 'messageThreads');
      const q = query(
        threadsRef,
        where('participants', 'array-contains', user.uid)
      );
      const snapshot = await getDocs(q);

      let existingThreadId: string | null = null;
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (
          data.participants?.includes(hostId) &&
          data.listing?.id === listing.id
        ) {
          existingThreadId = doc.id;
        }
      });

      if (existingThreadId) {
        navigate(`/dashboard/messages/${existingThreadId}`);
        return;
      }

      // Create new thread
      const threadData = {
        participants: [user.uid, hostId],
        otherParticipant: {
          id: hostId,
          name: listing.host.displayName,
          photo: listing.host.photoURL || '',
        },
        listing: {
          id: listing.id,
          title: listing.title,
          photo: listing.photos?.[0]?.url || '',
        },
        lastMessage: {
          content: '',
          senderId: user.uid,
          createdAt: serverTimestamp(),
        },
        lastMessageAt: serverTimestamp(),
        unreadCount: 0,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(threadsRef, threadData);
      navigate(`/dashboard/messages/${docRef.id}`);
    } catch (err) {
      console.error('Failed to create message thread:', err);
    }
  };

  if (isLoading) {
    return (
      <PublicLayout>
        <Container className="py-8">
          <ListingSkeleton />
        </Container>
      </PublicLayout>
    );
  }

  if (error || !listing) {
    return (
      <PublicLayout>
        <Container className="py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Listing not found
            </h1>
            <p className="text-gray-500 mb-6">
              The listing you're looking for doesn't exist or has been removed.
            </p>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600"
            >
              Browse all listings
            </Link>
          </div>
        </Container>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout hideBottomNav>
      {/* Photo Gallery */}
      <Container className="pt-6">
        <PhotoGallery photos={listing.photos} />
      </Container>

      {/* Main Content */}
      <Container className="py-6">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
          {/* Left Column - Content */}
          <div className="flex-1 min-w-0">
            <PropertyInfo
              listing={listing}
              onSave={() => setIsSaved(!isSaved)}
              isSaved={isSaved}
            />

            <Description description={listing.description} />

            <Amenities amenityIds={listing.amenityIds} />

            <LocationMap location={listing.location} />

            <HostCard host={listing.host} onContact={handleContactHost} />

            <Reviews listingId={listing.id} />

            <HouseRules
              houseRules={listing.houseRules}
              cancellationPolicy={listing.cancellationPolicy}
              minimumStay={listing.minimumStay}
            />
          </div>

          {/* Right Column - Booking Widget (Desktop) */}
          <div className="hidden lg:block lg:w-[380px] shrink-0">
            <BookingWidget listing={listing} />
          </div>
        </div>
      </Container>

      {/* Mobile Booking Bar */}
      <MobileBookingBar listing={listing} />
    </PublicLayout>
  );
};

// Loading Skeleton
const ListingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    {/* Photo Gallery Skeleton */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded-xl overflow-hidden mb-6">
      <div className="col-span-2 row-span-2 aspect-[4/3] bg-gray-200" />
      <div className="hidden md:block aspect-[4/3] bg-gray-200" />
      <div className="hidden md:block aspect-[4/3] bg-gray-200" />
      <div className="hidden md:block aspect-[4/3] bg-gray-200" />
      <div className="hidden md:block aspect-[4/3] bg-gray-200" />
    </div>

    <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
      <div className="flex-1">
        {/* Title */}
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-6" />

        {/* Stats */}
        <div className="flex gap-4 mb-6">
          <div className="h-4 bg-gray-200 rounded w-20" />
          <div className="h-4 bg-gray-200 rounded w-20" />
          <div className="h-4 bg-gray-200 rounded w-20" />
        </div>

        {/* Description */}
        <div className="space-y-2 mb-8">
          <div className="h-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>

        {/* Amenities */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-6 bg-gray-200 rounded" />
          ))}
        </div>
      </div>

      {/* Booking Widget */}
      <div className="hidden lg:block lg:w-[380px]">
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    </div>
  </div>
);

export default ListingPage;
